import { queryStringParamsCache } from '../app-kit/helpers/getCacheQueryStringParamsFromUrl';
import { SearchPropertyStatus } from '../types';
import {
  isLocationNotDefault,
  isBoundingBoxNotDefault,
  isPolygonsNotDefault,
  isSurroundingSuburbsNotDefault,
  isPropertyStatusNotDefault,
  isPropertyTypeNotDefault,
  isMinBedroomsNotDefault,
  isMinBathroomsNotDefault,
  isMinLandsizeNotDefault,
  isIndoorFeaturesNotDefault,
  isOutdoorFeaturesNotDefault,
  isEcoFriendlyFeaturesNotDefault,
  isMinCarSpacesNotDefault,
  isMinPriceNotDefault,
  isMaxPriceNotDefault,
  isKeywordsNotDefault,
  isExcludeUnderOfferNotDefault,
  isExcludeDepositTakenNotDefault,
  isHasImagesNotDefault,
  isSortOptionNotDefault,
  isSearchViewNotDefault,
  isSearchFlowNotDefault,
  isSearchCommutesNotDefault,
  isPageNumberNotDefault,
} from './searchSlugDefaultChecks';
import { SEARCH_FLOW_DEFAULT } from './defaults';
import { SearchQueryKeys } from './searchQueryKeys';

type SearchQueryKeysRecord = Partial<Record<SearchQueryKeys, string>>;

interface UpdateSearchUrlProps {
  currentUrl?: string;
  updates: SearchQueryKeysRecord;
  historyPushFn?: (url: string) => void;
  saveRecentLocationSearch?: (url: string) => void | Promise<void>;
}

export const createSearchUrl = ({
  currentUrl = '',
  updates,
  historyPushFn,
  saveRecentLocationSearch,
}: UpdateSearchUrlProps): string => {
  const currentParams = queryStringParamsCache.extractQueryAsParamsFromUrl(currentUrl);

  const state: SearchQueryKeysRecord = {
    ...currentParams,
    ...updates,
  };

  if (updates.polygons) {
    state.locations = undefined;
    state.boundingBox = undefined;
    state.commutes = undefined;
    state.flow = SEARCH_FLOW_DEFAULT;
  } else if (updates.commutes) {
    state.locations = undefined;
    state.boundingBox = undefined;
    state.polygons = undefined;
    state.flow = 'commute';
  } else if (updates.boundingBox) {
    state.locations = undefined;
  } else if (updates.locations) {
    state.boundingBox = undefined;
    state.polygons = undefined;
    state.commutes = undefined;
    state.flow = SEARCH_FLOW_DEFAULT;
  }

  const newParams = new URLSearchParams();
  const preservedParams = new URLSearchParams();
  Object.entries(state).forEach(([key, value]) => {
    if (!value) return;
    switch (key as SearchQueryKeys) {
      case 'locations':
        if (isLocationNotDefault(value)) newParams.append('locations', value);
        break;
      case 'boundingBox':
        if (isBoundingBoxNotDefault(value)) newParams.append('boundingBox', value);
        break;
      case 'polygons':
        if (isPolygonsNotDefault(value)) newParams.append('polygons', value);
        break;
      case 'surroundingSuburbs':
        if (isSurroundingSuburbsNotDefault(value)) newParams.append('surroundingSuburbs', value);
        break;
      case 'propertyStatus':
        if (isPropertyStatusNotDefault(value)) newParams.append('propertyStatus', value);
        break;
      case 'propertyTypes':
        if (isPropertyTypeNotDefault(value)) newParams.append('propertyTypes', value);
        break;
      case 'minBedrooms':
        if (isMinBedroomsNotDefault(value)) newParams.append('minBedrooms', value);
        break;
      case 'minBathrooms':
        if (isMinBathroomsNotDefault(value)) newParams.append('minBathrooms', value);
        break;
      case 'minLandSize':
        if (isMinLandsizeNotDefault(value)) newParams.append('minLandSize', value);
        break;
      case 'indoorFeatures':
        if (isIndoorFeaturesNotDefault(value)) newParams.append('indoorFeatures', value);
        break;
      case 'outdoorFeatures':
        if (isOutdoorFeaturesNotDefault(value)) newParams.append('outdoorFeatures', value);
        break;
      case 'ecoFriendlyFeatures':
        if (isEcoFriendlyFeaturesNotDefault(value)) newParams.append('ecoFriendlyFeatures', value);
        break;
      case 'minCarSpaces':
        if (isMinCarSpacesNotDefault(value)) newParams.append('minCarSpaces', value);
        break;
      case 'minPrice':
        if (isMinPriceNotDefault(value)) newParams.append('minPrice', value);
        break;
      case 'maxPrice':
        if (isMaxPriceNotDefault(value)) newParams.append('maxPrice', value);
        break;
      case 'keywords':
        if (isKeywordsNotDefault(value)) newParams.append('keywords', value);
        break;
      case 'excludeUnderOffer':
        if (isExcludeUnderOfferNotDefault(value)) newParams.append('excludeUnderOffer', value);
        break;
      case 'excludeDepositTaken':
        if (isExcludeDepositTakenNotDefault(value)) newParams.append('excludeDepositTaken', value);
        break;
      case 'hasImages':
        if (isHasImagesNotDefault(value)) newParams.append('hasImages', value);
        break;
      case 'sortOption':
        if (
          isSortOptionNotDefault(
            value,
            (state?.propertyStatus as SearchPropertyStatus) ?? 'FOR_SALE',
            Boolean(state?.polygons),
          )
        )
          newParams.append('sortOption', value);
        break;
      case 'view':
        if (isSearchViewNotDefault(value)) newParams.append('view', value);
        break;
      case 'flow':
        if (isSearchFlowNotDefault(value)) newParams.append('flow', value);
        break;
      case 'commutes':
        if (isSearchCommutesNotDefault(value)) newParams.append('commutes', value);
        break;
      case 'pageNumber':
        if (isPageNumberNotDefault(value)) newParams.append('pageNumber', value);
        break;
      case 'zoomLevel':
        if (state.boundingBox) newParams.append('zoomLevel', value);
        break;
      case 'findSimilar':
        if (value && value.length) newParams.append('findSimilar', value);
        break;
      default:
        // Preserve keys that aren't related to search (ie feature flags)
        if (!SearchQueryKeys.includes(key as SearchQueryKeys)) {
          preservedParams.append(key, value);
        }
    }
  });

  const currentParamsAsString = new URLSearchParams(currentParams).toString();
  const newParamsAsString = newParams.toString();
  const preservedParamsAsString = preservedParams.toString();

  const allParamsAsString = [newParamsAsString, preservedParamsAsString].filter(Boolean).join('&');

  const saveUrl = `/search/${newParamsAsString.length ? `?${newParamsAsString}` : ''}`;
  const newUrl = `/search/${allParamsAsString.length ? `?${allParamsAsString}` : ''}`;

  if (allParamsAsString !== currentParamsAsString && historyPushFn) {
    if (saveRecentLocationSearch) {
      void saveRecentLocationSearch(saveUrl);
    }
    historyPushFn(newUrl);
  }

  return newUrl;
};
